/**
 * X (Twitter) pixel and its web event tags.
 *
 * The pixel id comes from the build-time env (`REACT_APP_X_PIXEL_ID`, read
 * once in `ux/src/config.js`). Unset means the whole pixel is off: no
 * script, no `window.twq`, no events. Production sets it to `r3zmh`.
 *
 * Tag IDs come from X Ads `list_web_event_tags` on account 18ce55qyg4m
 * (pixel / website_tag_id `r3zmh`, 13 tags, all UNVERIFIED as of 2026-10-17).
 * The uwt.js event id is X's `tw-<pixel>-<tag>` form. The API embed_code is
 * the legacy oct.js snippet and is not what the site loads.
 *
 * Only PAGE_VIEW, DOWNLOAD, SIGN_UP, and PURCHASE are fired. The rest are
 * listed so Events Manager IDs stay in one place.
 */

import config from '../config';

export const X_PIXEL_SCRIPT_SRC = 'https://static.ads-twitter.com/uwt.js';

// Tag suffixes per pixel. The uwt.js event id is `tw-<pixelId>-<tag>`, built
// from the configured pixel id, so a different pixel can never send events
// tagged for r3zmh. A pixel with no entry here sends no events (config only).
const X_EVENT_TAGS_BY_PIXEL = {
  r3zmh: {
    LANDING_PAGE_VIEW: '12fq3v',
    SITE_VISIT: '12fq3w',
    DOWNLOAD: '12fq3x',
    SIGN_UP: '12fq3y',
    PURCHASE: '12fq3z',
    ADDED_PAYMENT_INFO: '12fq40',
    CHECKOUT_INITIATED: '12fq41',
    ADD_TO_WISHLIST: '12fq42',
    ADD_TO_CART: '12fq43',
    SEARCH: '12fq44',
    CONTENT_VIEW: '12fq45',
    COMPLETE_REGISTRATION: '12fq46',
    PAGE_VIEW: '12fq47',
  },
};

function eventIdsFor(pixelId) {
  const tags = Object.prototype.hasOwnProperty.call(X_EVENT_TAGS_BY_PIXEL, pixelId)
    ? X_EVENT_TAGS_BY_PIXEL[pixelId]
    : {};
  const ids = {};
  Object.keys(tags).forEach((name) => {
    ids[name] = `tw-${pixelId}-${tags[name]}`;
  });
  return ids;
}

function cleanPixelId(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  return /^[a-z0-9]+$/i.test(text) ? text : '';
}

/**
 * Pixel id and event ids. Callers use this instead of the constants.
 * #2450 will point it at the admin tracking config. Do not read the ids
 * from anywhere else. An empty pixelId means "not configured".
 */
export function getXAdsPixelIds() {
  const pixelId = cleanPixelId(config.X_PIXEL_ID);
  return {
    pixelId,
    eventIds: pixelId ? eventIdsFor(pixelId) : {},
  };
}

export function isXPixelConfigured() {
  return getXAdsPixelIds().pixelId !== '';
}
